import { Injectable } from '@angular/core';
import { Budget, BudgetCalculationItem, BudgetResponse, CategorySelectionOption, IncomeDetail, MemberSelectionOption,
  ResourceDetail, SubScreenRequest, InKindSupportMaintenance,
  QualifiedIncomeTrust, DeemedIncomeFM, MedicalSpenddown, 
  DeemedResources} from '../models/budget-response.interface';
import { TokenService } from '@app/services/token/token.service';
import { ApiService } from '@app/gateway/services/api.service';
import { Observable } from 'rxjs';
import { HttpHeaders } from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class BudgetMapperService {

  constructor(
    private token: TokenService, 
    private apiService: ApiService) { }
 
  getNodBudgetInfo(payload): Observable<any> {
    return this.apiService.post('nodBudgetInfo', payload, { headers: new HttpHeaders({ 'pageId': 'TCCBD', 'sessionId': this.token.getSessionId() }) }, true);
  }

  getBudgetScreenInfo(payload: SubScreenRequest): Observable<any> {
    return this.apiService.post('nodBudgetInfo/subScreenInfo', payload, { headers: new HttpHeaders({ 'pageId': 'TCCBD', 'sessionId': this.token.getSessionId() }) }, true);
  }

  //Extract household members from the budget response
  extractHouseholdMembers(response: BudgetResponse): MemberSelectionOption[] {
    return response?.nodBudgetInfoWrapper?.indvs?.map(indv => ({
      id: indv?.indvId.toString(),
      name: this.buildFullName(indv?.firstName, indv?.midName, indv?.lastName),
      firstName: indv?.firstName,
      lastName: indv?.lastName,
      midName: indv?.midName,
      dob: indv?.dob,
      gender: indv?.gender,
      hasBudgetInfo: indv?.budgets && indv?.budgets.length > 0
    }));
  }

  // Extract available categories for a selected member
  extractCategories(response: BudgetResponse, indvId: number): CategorySelectionOption[] {
    const individual = response?.nodBudgetInfoWrapper?.indvs?.find(indv => indv.indvId === indvId);
    if (!individual || !individual?.budgets) {
      return [];
    }

    // Filter out R01 and SST COE codes
    const excludedCodes = ['R01', 'SST'];
    return individual.budgets
      .filter(budget => !excludedCodes.includes(budget?.coe))
      .map(budget => ({
        code: budget?.coe,
        name: budget?.coeDescription,
        type: budget?.type
      }));
  }

  // Get the budget data for a specific member and category
  getBudgetForMemberAndCategory(response: BudgetResponse, indvId: number, categoryCode: string): Budget | null {
    const individual = response?.nodBudgetInfoWrapper?.indvs?.find(indv => indv.indvId === indvId);
    if (!individual) {
      return null;
    }

    const budget = individual.budgets.find(
      budget => budget.coe === categoryCode
    );
    
    if (budget) {
      // Enrich budget with indvId for subscreen navigation
      (budget as any).indvId = indvId;
    }
    
    return budget || null;
  }

  // Build full name from first, middle, and last name
  private buildFullName(firstName: string, midName: string | undefined, lastName: string): string {
    const parts = [firstName, midName, lastName].filter(part => part && part.trim());
    return parts.join(' ');
  }

  //Map budget data to income items for UI display
  mapIncomeItems(budget: Budget): BudgetCalculationItem[] {
    switch (budget?.type) {
      case 'MAGI':
        return this.mapMagiIncomeItems(budget);
      case 'CK':
        return this.mapMagiIncomeItems(budget);
      case 'ABD':
        return this.mapAbdIncomeItems(budget);
      case 'FM':
        return this.mapFmIncomeItems(budget);
      default:
        return [];
    }
  }

  public mapMagiIncomeItems(budget: Budget): BudgetCalculationItem[] {
    const items: BudgetCalculationItem[] = [];
    if (budget?.netCountableSelfEmploymentIncomeTotal !== undefined) {
      items.push({
        id: 'self-employment',
        label: 'MAGI_SELF_EMPLOYMENT',
        amount: budget.netCountableSelfEmploymentIncomeTotal,
        hasInfo: false,
        isExpandable: true,
        tableData: this.mapIncomeTableData(budget.netCountableSelfEmploymentIncomes)
      });
    }

    // Earned Income
    if (budget.netCountableEarnedIncomeTotal !== undefined) {
      items.push({
        id: 'earned-income',
        label: 'MAGI_EARNED_INCOME',
        amount: budget.netCountableEarnedIncomeTotal,
        hasInfo: false,
        isExpandable: true,
        tableData: this.mapIncomeTableData(budget.netCountableEarnedIncomes)
      });
    }

    // Before Pre-Tax Contribution Deductions
    if (budget.beforePreTaxContributionDeductions !== undefined) {
      items.push({
        id: 'pre-tax-deductions',
        label: 'MAGI_PRE_TAX_DEDUCTIONS',
        amount: budget.beforePreTaxContributionDeductions,
        hasInfo: false,
        isExpandable: false,
        operator: '-'
      });
    }

    // Remaining Countable Earned/Self-Employment Amount
    if (budget.remainingCountableEarnedSelfEmploymentAmount !== undefined) {
      items.push({
        id: 'remaining-earned',
        label: 'MAGI_REMAINING_EARNED',
        amount: budget.remainingCountableEarnedSelfEmploymentAmount,
        hasInfo: false,
        isExpandable: false
      });
    }

    // Unearned Income
    if (budget.netCountableUnearnedIncomeTotal !== undefined) {
      items.push({
        id: 'unearned-income',
        label: 'MAGI_UNEARNED_INCOME',
        amount: budget.netCountableUnearnedIncomeTotal,
        hasInfo: false,
        isExpandable: true,
        tableData: this.mapIncomeTableData(budget.netCountableUnearnedIncomes)
      });
    }

    // COLA Deduction
    if (budget.colaDeduction !== undefined) {
      items.push({
        id: 'cola-deduction',
        label: 'MAGI_COLA_DEDUCTION',
        amount: budget.colaDeduction,
        hasInfo: true,
        isExpandable: false,
        operator: '-',
        tooltipText: 'COLA_DEDUCTION_TOOLTIP'
      });
    }

    // Countable Earned Income and Unearned Income
    if (budget.countableEarnedIncomeAndUnearnedIncome !== undefined) {
      items.push({
        id: 'countable-earned-unearned',
        label: 'MAGI_COUNTABLE_EARNED_UNEARNED',
        amount: budget.countableEarnedIncomeAndUnearnedIncome,
        hasInfo: false,
        operator: '-',
        isExpandable: false
      });
    }

    // 1040 Deductions
    if (budget.deductions1040 !== undefined) {
      items.push({
        id: 'deductions-1040',
        label: 'MAGI_DEDUCTIONS_1040',
        amount: budget.deductions1040,
        hasInfo: false,
        isExpandable: false,
        operator: '-'
      });
    }

    // MAGI 5% Deduction
    if (budget.magi5PercentDeduction !== undefined) {
      items.push({
        id: 'magi-5-percent',
        label: 'MAGI_5_PERCENT_DEDUCTION',
        amount: budget.magi5PercentDeduction,
        hasInfo: false,
        isExpandable: false,
        operator: '-'
      });
    }

    // Net Countable Income
    if (budget.netCountableIncome !== undefined) {
      items.push({
        id: 'net-countable-income',
        label: 'MAGI_NET_COUNTABLE_INCOME',
        amount: budget.netCountableIncome,
        hasInfo: false,
        isExpandable: false,
        isTotal: true
      });
    }

    // Income Test Limit
    if (budget.incomeTestLimit !== undefined) {
      items.push({
        id: 'income-test-limit',
        label: 'MAGI_INCOME_TEST_LIMIT',
        amount: budget.incomeTestLimit,
        hasInfo: false,
        isExpandable: false
      });
    }

    // Gap Filling Amount
    if (budget.gapFillingAmount !== undefined) {
      items.push({
        id: 'gap-filling',
        label: 'MAGI_GAP_FILLING',
        amount: budget.gapFillingAmount,
        hasInfo: false,
        isExpandable: false,
        isCurrency: false,
        isResult: true
      });
    }

    // Total Net Countable Income
    if (budget.totalNetCountableIncome !== undefined) {
      items.push({
        id: 'total-net-countable',
        label: 'MAGI_TOTAL_NET_COUNTABLE',
        amount: budget.totalNetCountableIncome,
        hasInfo: false,
        isExpandable: false,
        isTotal: true
      });
    }

    // Income Test Result
    if (budget?.incomeTestResult !== undefined) {
      items.push({
        id: 'income-test-result',
        label: 'MAGI_INCOME_TEST_RESULT',
        amount: budget.incomeTestResult,
        hasInfo: false,
        isExpandable: false,
        isResult: true,
        isCurrency: false
      });
    }
    return items;
  }

  /**
   * Map ABD-specific income items
   */
  private mapAbdIncomeItems(budget: Budget): BudgetCalculationItem[] {
    const items: BudgetCalculationItem[] = [];

    // Unearned Income
    if (budget.netCountableUnearnedIncomeTotal !== undefined) {
      items.push({
        id: 'unearned-income',
        label: 'ABD_UNEARNED_INCOME',
        amount: budget.netCountableUnearnedIncomeTotal,
        hasInfo: false,
        isExpandable: true,
        tableData: this.mapIncomeTableData(budget.netCountableUnearnedIncomes)
      });
    }

    // COLA Deduction
    if (budget.colaDeduction !== undefined) {
      items.push({
        id: 'cola-deduction',
        label: 'ABD_COLA_DEDUCTION',
        amount: budget.colaDeduction,
        hasInfo: true,
        isExpandable: false,
        tooltipText: 'COLA_DEDUCTION_TOOLTIP'
      });
    }

    // Irregular Unearned Income Disregard
    if (budget.irregularUnearnedIncomeDisregard !== undefined) {
      items.push({
        id: 'irregular-unearned',
        label: 'ABD_IRREGULAR_UNEARNED',
        amount: budget.irregularUnearnedIncomeDisregard,
        hasInfo: false,
        isExpandable: false
      });
    }

    // In-Kind Support and Maintenance
    if (budget.inKindSupportAndMaintenanceAmount !== undefined) {
      items.push({
        id: 'in-kind-support',
        label: 'ABD_IN_KIND_SUPPORT',
        amount: budget.inKindSupportAndMaintenanceAmount,
        hasInfo: false,
        isExpandable: false,
        isLink: budget?.inKindSupportAndMaintenance !== null,
        subScreenType: 'inKindSupportAndMaintenance',
        coe: budget?.coe,
        indivId: (budget as any)?.indvId
      });
    }

    // Ineligible Spouse's Deemed Unearned Income
    if (budget.ineligibleSpousesDeemedUnearnedIncome !== undefined) {
      items.push({
        id: 'ineligible-spouse-unearned',
        label: 'ABD_INELIGIBLE_SPOUSE_UNEARNED',
        amount: budget.ineligibleSpousesDeemedUnearnedIncome,
        hasInfo: false,
        isExpandable: false
      });
    }

    // Ineligible Parent's Deemed Income
    if (budget.ineligibleParentsDeemedIncome !== undefined) {
      items.push({
        id: 'ineligible-parent',
        label: 'ABD_INELIGIBLE_PARENT',
        amount: budget.ineligibleParentsDeemedIncome,
        hasInfo: false,
        isExpandable: false
      });
    }

    // General Deduction
    if (budget.generalDeduction !== undefined) {
      items.push({
        id: 'general-deduction',
        label: 'ABD_GENERAL_DEDUCTION',
        amount: budget.generalDeduction,
        hasInfo: false,
        isExpandable: false
      });
    }

    // Child Support Disregard
    if (budget.childSupportDisregard !== undefined) {
      items.push({
        id: 'child-support',
        label: 'ABD_CHILD_SUPPORT',
        amount: budget.childSupportDisregard,
        hasInfo: false,
        isExpandable: false
      });
    }

    // Widow/Widower Entitlement Disregard
    if (budget.widowWidowerEntitlementDisregard !== undefined) {
      items.push({
        id: 'widow-disregard',
        label: 'ABD_WIDOW_DISREGARD',
        amount: budget.widowWidowerEntitlementDisregard,
        hasInfo: false,
        isExpandable: false
      });
    }

    // DAC Entitlement Disregard
    if (budget.dacEntitlementDisregard !== undefined) {
      items.push({
        id: 'dac-disregard',
        label: 'ABD_DAC_DISREGARD',
        amount: budget.dacEntitlementDisregard,
        hasInfo: true,
        isExpandable: false,
        tooltipText: 'ABD_DAC_TOOLTIP'
      });
    }

    // COLA Disregard
    if (budget.colaDisregard !== undefined) {
      items.push({
        id: 'cola-disregard',
        label: 'ABD_COLA_DISREGARD',
        amount: budget.colaDisregard,
        hasInfo: true,
        isExpandable: false,
        tooltipText: 'COLA_DEDUCTION_TOOLTIP'
      });
    }

    // Total Net Unearned Income
    if (budget.totalNetUnearnedIncome !== undefined) {
      items.push({
        id: 'total-net-unearned',
        label: 'ABD_TOTAL_NET_UNEARNED',
        amount: budget.totalNetUnearnedIncome,
        hasInfo: false,
        isExpandable: false,
        isTotal: true
      });
    }

    // Self Employment Income
    if (budget.netCountableSelfEmploymentIncomeTotal !== undefined) {
      items.push({
        id: 'self-employment',
        label: 'ABD_SELF_EMPLOYMENT',
        amount: budget.netCountableSelfEmploymentIncomeTotal,
        hasInfo: false,
        isExpandable: true,
        tableData: this.mapIncomeTableData(budget.netCountableSelfEmploymentIncomes)
      });
    }

    // Earned Income
    if (budget.netCountableEarnedIncomeTotal !== undefined) {
      items.push({
        id: 'earned-income',
        label: 'ABD_EARNED_INCOME',
        amount: budget.netCountableEarnedIncomeTotal,
        hasInfo: false,
        isExpandable: true,
        tableData: this.mapIncomeTableData(budget.netCountableEarnedIncomes)
      });
    }

    // Irregular Earned Income Disregard
    if (budget.irregularEarnedIncomeDisregard !== undefined) {
      items.push({
        id: 'irregular-earned',
        label: 'ABD_IRREGULAR_EARNED',
        amount: budget.irregularEarnedIncomeDisregard,
        hasInfo: false,
        isExpandable: false
      });
    }

    // Student Earned Income Exclusion
    if (budget.studentEarnedIncomeExclusion !== undefined) {
      items.push({
        id: 'student-earned',
        label: 'ABD_STUDENT_EARNED',
        amount: budget.studentEarnedIncomeExclusion,
        hasInfo: false,
        isExpandable: false
      });
    }

    // Ineligible Spouse's Deemed Earned Income (link to abdDeemedIncome)
    if (budget.ineligibleSpousesDeemedEarnedIncome !== undefined) {
      items.push({
        id: 'ineligible-spouse-earned',
        label: 'ABD_INELIGIBLE_SPOUSE_EARNED',
        amount: budget.ineligibleSpousesDeemedEarnedIncome,
        hasInfo: false,
        isExpandable: false,
        isLink: budget?.abdDeemedIncome !== null,
        subScreenType: "abdDeemedIncome",
        coe: budget?.coe,
        indivId: (budget as any)?.indvId
      });
    }

    // Remaining General Deduction
    if (budget.remainingGeneralDeduction !== undefined) {
      items.push({
        id: 'remaining-general',
        label: 'ABD_REMAINING_GENERAL',
        amount: budget.remainingGeneralDeduction,
        hasInfo: false,
        isExpandable: false
      });
    }

    // Earned Income Deduction
    if (budget.earnedIncomeDeduction !== undefined) {
      items.push({
        id: 'earned-income-deduction',
        label: 'ABD_EARNED_INCOME_DEDUCTION',
        amount: budget.earnedIncomeDeduction,
        hasInfo: false,
        isExpandable: false
      });
    }

    // Impairment Related Work Expense
    if (budget.impairmentRelatedWorkExpense !== undefined) {
      items.push({
        id: 'impairment-work',
        label: 'ABD_IMPAIRMENT_WORK',
        amount: budget.impairmentRelatedWorkExpense,
        hasInfo: false,
        isExpandable: false
      });
    }

    // Half Deduction
    if (budget.halfDeduction !== undefined) {
      items.push({
        id: 'half-deduction',
        label: 'ABD_HALF_DEDUCTION',
        amount: budget.halfDeduction,
        hasInfo: false,
        isExpandable: false
      });
    }

    // Blind Work Expense
    if (budget.blindWorkExpense !== undefined) {
      items.push({
        id: 'blind-work',
        label: 'ABD_BLIND_WORK',
        amount: budget.blindWorkExpense,
        hasInfo: false,
        isExpandable: false
      });
    }

    // Total Net Earned Income
    if (budget.totalNetEarnedIncome !== undefined) {
      items.push({
        id: 'total-net-earned',
        label: 'ABD_TOTAL_NET_EARNED',
        amount: budget.totalNetEarnedIncome,
        hasInfo: false,
        isExpandable: false,
        isTotal: true
      });
    }

    // Total Countable Income
    if (budget.totalCountableIncome !== undefined) {
      items.push({
        id: 'total-countable',
        label: 'ABD_TOTAL_COUNTABLE',
        amount: budget.totalCountableIncome,
        hasInfo: false,
        isExpandable: false,
        isTotal: true
      });
    }

    // Qualified Income Trust Amount
    if (budget.qualifiedIncomeTrustAmount !== undefined) {
      items.push({
        id: 'qit-amount',
        label: 'ABD_QIT_AMOUNT',
        amount: budget.qualifiedIncomeTrustAmount,
        hasInfo: false,
        isExpandable: false,
        isLink: budget?.qualifiedIncomeTrust !== null,
        subScreenType: "qualifiedIncomeTrust",
        coe: budget?.coe,
        indivId: (budget as any)?.indvId
      });
    }

    // FPL 250% Disregard
    if (budget.fpl250PercentDisregard !== undefined) {
      items.push({
        id: 'fpl-250',
        label: 'ABD_FPL_250',
        amount: budget.fpl250PercentDisregard,
        hasInfo: false,
        isExpandable: false
      });
    }

    // Net Countable Income
    if (budget.netCountableIncome !== undefined) {
      items.push({
        id: 'net-countable-income',
        label: 'ABD_NET_COUNTABLE_INCOME',
        amount: budget.netCountableIncome,
        hasInfo: false,
        isExpandable: false,
        isTotal: true
      });
    }

    // Income Test Limit
    if (budget.incomeTestLimit !== undefined) {
      items.push({
        id: 'income-test-limit',
        label: 'ABD_INCOME_TEST_LIMIT',
        amount: budget.incomeTestLimit,
        hasInfo: false,
        isExpandable: false
      });
    }

    // Income Test Result
    if (budget.incomeTestResult !== undefined) {
      items.push({
        id: 'income-test-result',
        label: 'ABD_INCOME_TEST_RESULT',
        amount: budget.incomeTestResult,
        hasInfo: false,
        isExpandable: false,
        isResult: true,
        isCurrency: false
      });
    }
    return items;
  }

  /**
   * Map FM-specific income items
   */
  private mapFmIncomeItems(budget: Budget): BudgetCalculationItem[] {
    const items: BudgetCalculationItem[] = [];

    // Self Employment Income
    if (budget.netCountableSelfEmploymentIncomeTotal !== undefined) {
      items.push({
        id: 'self-employment',
        label: 'FM_SELF_EMPLOYMENT',
        amount: budget.netCountableSelfEmploymentIncomeTotal,
        hasInfo: false,
        isExpandable: true,
        operator: '=',
        tableData: this.mapIncomeTableData(budget.netCountableSelfEmploymentIncomes)
      });
    }

    // Earned Income
    if (budget.netCountableEarnedIncomeTotal !== undefined) {
      items.push({
        id: 'earned-income',
        label: 'FM_EARNED_INCOME',
        amount: budget.netCountableEarnedIncomeTotal,
        hasInfo: false,
        isExpandable: true,
        operator: '=',
        tableData: this.mapIncomeTableData(budget.netCountableEarnedIncomes)
      });
    }

    // Combined Earned/Self-Employment Income
    if (budget.combinedEarnedSelfEmploymentIncome !== undefined) {
      items.push({
        id: 'combined-earned',
        label: 'FM_COMBINED_EARNED',
        amount: budget.combinedEarnedSelfEmploymentIncome,
        hasInfo: false,
        operator: '=',
        isExpandable: false
      });
    }

    // Irregular Earned Income Disregard
    if (budget.irregularEarnedIncomeDisregard !== undefined) {
      items.push({
        id: 'irregular-earned',
        label: 'FM_IRREGULAR_EARNED',
        amount: budget.irregularEarnedIncomeDisregard,
        hasInfo: false,
        operator: '=',
        isExpandable: false
      });
    }

    // Standard Work Expense Deduction
    if (budget.standardWorkExpenseDeduction !== undefined) {
      items.push({
        id: 'work-expense',
        label: 'FM_WORK_EXPENSE',
        amount: budget.standardWorkExpenseDeduction,
        hasInfo: false,
        isExpandable: false,
        operator: '-'
      });
    }

    // Dependent Care Deduction
    if (budget.dependentCareDeduction !== undefined) {
      items.push({
        id: 'dependent-care',
        label: 'FM_DEPENDENT_CARE',
        amount: budget.dependentCareDeduction,
        hasInfo: false,
        isExpandable: false,
        operator: '-'
      });
    }

    // Remaining Countable Earned/Self-Employment Amount
    if (budget.remainingCountableEarnedSelfEmploymentAmount !== undefined) {
      items.push({
        id: 'remaining-earned',
        label: 'FM_REMAINING_EARNED',
        amount: budget.remainingCountableEarnedSelfEmploymentAmount,
        hasInfo: false,
        isExpandable: false,
        operator: '='
      });
    }

    // Unearned Income
    if (budget.netCountableUnearnedIncomeTotal !== undefined) {
      items.push({
        id: 'unearned-income',
        label: 'FM_UNEARNED_INCOME',
        amount: budget.netCountableUnearnedIncomeTotal,
        hasInfo: false,
        isExpandable: true,
        operator: '=',
        tableData: this.mapIncomeTableData(budget.netCountableUnearnedIncomes)
      });
    }

    // Irregular Unearned Income Disregard
    if (budget.irregularUnearnedIncomeDisregard !== undefined) {
      items.push({
        id: 'irregular-unearned',
        label: 'FM_IRREGULAR_UNEARNED',
        amount: budget.irregularUnearnedIncomeDisregard,
        hasInfo: false,
        isExpandable: false,
        operator: '-'
      });
    }

    // Child Support Disregard
    if (budget.childSupportDisregard !== undefined) {
      items.push({
        id: 'child-support',
        label: 'FM_CHILD_SUPPORT',
        amount: budget.childSupportDisregard,
        hasInfo: false,
        operator: '=',
        isExpandable: false
      });
    }

    // Total Deemed Income
    if (budget.totalDeemedIncome !== undefined) {
      items.push({
        id: 'deemed-income',
        label: 'FM_DEEMED_INCOME',
        amount: budget.totalDeemedIncome,
        hasInfo: false,
        isExpandable: false,
        operator: '=',
        isLink: budget?.fmDeemedIncome !== null,
        subScreenType: 'fmDeemedIncome',
        coe: budget?.coe,
        indivId: (budget as any)?.indvId
      });
    }

    // Remaining Countable Unearned Income
    if (budget.remainingCountableUnearnedIncome !== undefined) {
      items.push({
        id: 'remaining-unearned',
        label: 'FM_REMAINING_UNEARNED',
        amount: budget.remainingCountableUnearnedIncome,
        hasInfo: false,
        isExpandable: false,
        operator: '-'
      });
    }

    // Child Support Mandatory Expense
    if (budget.childSupportMandatoryExpense !== undefined) {
      items.push({
        id: 'child-support-expense',
        label: 'FM_CHILD_SUPPORT_EXPENSE',
        amount: budget.childSupportMandatoryExpense,
        hasInfo: false,
        operator: '=',
        isExpandable: false
      });
    }

    // Total Net Countable Income
    if (budget.totalNetCountableIncome !== undefined) {
      items.push({
        id: 'total-net-countable',
        label: 'FM_TOTAL_NET_COUNTABLE',
        amount: budget.totalNetCountableIncome,
        hasInfo: false,
        operator: '=',
        isExpandable: false,
        isTotal: true
      });
    }

    // Net Income Limit
    if (budget.netIncomeLimit !== undefined) {
      items.push({
        id: 'net-income-limit',
        label: 'FM_NET_INCOME_LIMIT',
        amount: budget.netIncomeLimit,
        hasInfo: false,
        operator: '=',
        isExpandable: false
      });
    }

    // Spenddown Met
    if (budget.spenddownMet !== undefined) {
      items.push({
        id: 'spenddown-met',
        label: 'FM_SPENDDOWN_MET',
        amount: budget.spenddownMet,
        hasInfo: false,
        isExpandable: false,
        isResult: true,
        isLink: budget.spenddownMet.toUpperCase() === 'YES' || budget.spenddownMet.toUpperCase() === 'NO',
        isCurrency: false,
        subScreenType: 'medicalSpenddown',
        coe: budget?.coe,
        indivId: (budget as any)?.indvId
      });
    }

    // Income Test Result
    if (budget.incomeTestResult !== undefined) {
      items.push({
        id: 'income-test-result',
        label: 'FM_INCOME_TEST_RESULT',
        amount: budget.incomeTestResult,
        hasInfo: false,
        isExpandable: false,
        isResult: true,
        isCurrency: false
      });
    }
    return items;
  }

  /**
   * Map SSI/FBR Income Test items
   */
  mapSsiFbrIncomeTest(budget: Budget): BudgetCalculationItem[] {
    const items: BudgetCalculationItem[] = [];
    
    if (!budget.ssiFbrIncomeTest) {
      return items;
    }

    const ssiFbr = budget?.ssiFbrIncomeTest;
    // Net Countable Unearned Income
    if (ssiFbr.netCountableUnearnedIncomeTotal !== undefined) {
      items.push({
        id: 'ssi-unearned-income',
        label: 'SSI_FBR_UNEARNED_INCOME',
        amount: ssiFbr.netCountableUnearnedIncomeTotal,
        hasInfo: false,
        isExpandable: true,
        operator: '=',
        tableData: this.mapIncomeTableData(ssiFbr.netCountableUnearnedIncomes)
      });
    }

    // Irregular Unearned Income Disregard
    if (ssiFbr.irregularUnearnedIncomeDisregard !== undefined) {
      items.push({
        id: 'ssi-irregular-unearned',
        label: 'SSI_FBR_IRREGULAR_UNEARNED',
        amount: ssiFbr.irregularUnearnedIncomeDisregard,
        hasInfo: false,
        isExpandable: false,
        operator: '=',
      });
    }

    // Ineligible Spouse's Deemed Unearned Income
    if (ssiFbr.ineligibleSpousesDeemedUnearnedIncome !== undefined) {
      items.push({
        id: 'ssi-spouse-unearned',
        label: 'SSI_FBR_INELIGIBLE_SPOUSE_UNEARNED',
        amount: ssiFbr.ineligibleSpousesDeemedUnearnedIncome,
        hasInfo: false,
        isExpandable: false,
        operator: '='
      });
    }

    // Ineligible Spouse's Deemed Earned Income
    if (ssiFbr.ineligibleSpousesDeemedEarnedIncome !== undefined) {
      items.push({
        id: 'ssi-spouse-earned',
        label: 'SSI_FBR_INELIGIBLE_SPOUSE_EARNED',
        amount: ssiFbr.ineligibleSpousesDeemedEarnedIncome,
        hasInfo: false,
        isExpandable: false,
        operator: '='
      });
    }

    // Ineligible Parent's Deemed Income
    if (ssiFbr.ineligibleParentsDeemedIncome !== undefined) {
      items.push({
        id: 'ssi-parent-income',
        label: 'SSI_FBR_INELIGIBLE_PARENT',
        amount: ssiFbr.ineligibleParentsDeemedIncome,
        hasInfo: false,
        isExpandable: false,
        operator: '='
      });
    }

    // General Deduction
    if (ssiFbr.generalDeduction !== undefined) {
      items.push({
        id: 'ssi-general-deduction',
        label: 'SSI_FBR_GENERAL_DEDUCTION',
        amount: ssiFbr.generalDeduction,
        hasInfo: false,
        isExpandable: false,
        operator: '='
      });
    }

    // Child Support Disregard
    if (ssiFbr.childSupportDisregard !== undefined) {
      items.push({
        id: 'ssi-child-support',
        label: 'SSI_FBR_CHILD_SUPPORT',
        amount: ssiFbr.childSupportDisregard,
        hasInfo: false,
        isExpandable: false,
        operator: '='
      });
    }

    // Total Net Unearned Income
    if (ssiFbr.totalNetUnearnedIncome !== undefined) {
      items.push({
        id: 'ssi-total-net-unearned',
        label: 'SSI_FBR_TOTAL_NET_UNEARNED',
        amount: ssiFbr.totalNetUnearnedIncome,
        hasInfo: false,
        isExpandable: false,
        operator: '='
      });
    }

    // SSI/FBR Limit
    if (ssiFbr.ssiFbrLimit !== undefined) {
      items.push({
        id: 'ssi-fbr-limit',
        label: 'SSI_FBR_LIMIT',
        amount: ssiFbr.ssiFbrLimit,
        hasInfo: true,
        isExpandable: false,
        operator: '=',
        tooltipText: 'SSI_FBR_LIMIT_TOOLTIP'
      });
    }

    // SSI/FBR Result
    if (ssiFbr.ssiFbrResult !== undefined) {
      items.push({
        id: 'ssi-fbr-result',
        label: 'SSI_FBR_RESULT',
        amount: ssiFbr.ssiFbrResult,
        hasInfo: true,
        isExpandable: false,
        isResult: true,
        isCurrency: false,
        tooltipText: 'SSI_FBR_LIMIT_TOOLTIP'
      });
    }

    return items;
  }

  mapDeemedResources(deemedRes: DeemedResources): BudgetCalculationItem[] {
    const items: BudgetCalculationItem[] = [];

    if (!deemedRes) {
      return items;
    }
    // Header for Countable Resources
    items.push({
      id: 'countabe-resources-header',
      label: 'COUNTABLE_RESOURCES',
      amount: '',
      hasInfo: false,
      isExpandable: false,
      isHeader: true,
      isCurrency: false
    });

    // Liquid Resources
    if (deemedRes.liquidResourcesTotal !== undefined) {
      items.push({
        id: 'deemed-liquid-resources',
        label: 'DEEMED_RESOURCES_LIQUID',
        amount: deemedRes.liquidResourcesTotal,
        hasInfo: false,
        isExpandable: true,
        tableData: this.mapResourceTableData(deemedRes.liquidResources),
        resourceType: 'liquid',
        operator: '+',

      });
    }

    // Non-Liquid Resources
    if (deemedRes.nonLiquidResourcesTotal !== undefined) {
      items.push({
        id: 'deemed-non-liquid',
        label: 'DEEMED_RESOURCES_NON_LIQUID',
        amount: deemedRes.nonLiquidResourcesTotal,
        hasInfo: false,
        isExpandable: true,
        operator: '+',
        tableData: this.mapResourceTableData(deemedRes.nonLiquidResources),
        resourceType: 'nonLiquid'
      });
    }

    // Burial Resource Exclusion
    if (deemedRes.burialReserveExclusion !== undefined) {
      items.push({
        id: 'deemed-burial-exclusion',
        label: 'DEEMED_RESOURCES_BURIAL_EXCLUSION',
        amount: deemedRes.burialReserveExclusion,
        hasInfo: false,
        isExpandable: false,
        operator: '-'
      });
    }

    // Vehicle Resources
    if (deemedRes.vehicleResourceTotal !== undefined) {
      items.push({
        id: 'deemed-vehicle-resources',
        label: 'DEEMED_RESOURCES_VEHICLE',
        amount: deemedRes.vehicleResourceTotal,
        hasInfo: false,
        isExpandable: true,
        operator: '+',
        tableData: this.mapResourceTableData(deemedRes.vehicleResources),
        resourceType: 'vehicle'
      });
    }

    // Total Countable Resource Amount
    if (deemedRes.totalCountableResourceAmount !== undefined) {
      items.push({
        id: 'deemed-total-countable',
        label: 'DEEMED_RESOURCES_TOTAL_COUNTABLE',
        amount: deemedRes.totalCountableResourceAmount,
        hasInfo: false,
        isExpandable: false,
        isTotal: true,
        operator: '='
      });
    }

    // Resource Limit
    if (deemedRes.resourceLimit !== undefined) {
      items.push({
        id: 'deemed-resource-limit',
        label: 'DEEMED_RESOURCES_LIMIT',
        amount: deemedRes.resourceLimit,
        hasInfo: false,
        isExpandable: false,
        operator: '='
      });
    }

    // Subtotal Deemed Amount
    if (deemedRes.subtotalDeemedAmount !== undefined) {
      items.push({
        id: 'deemed-subtotal',
        label: 'DEEMED_RESOURCES_SUBTOTAL',
        amount: deemedRes.subtotalDeemedAmount,
        hasInfo: false,
        isExpandable: false,
        operator: '='
      });
    }

    // Number of Eligible Children
    if (deemedRes.numberOfEligibleChildren !== undefined) {
      items.push({
        id: 'deemed-eligible-children',
        label: 'DEEMED_RESOURCES_ELIGIBLE_CHILDREN',
        amount: deemedRes.numberOfEligibleChildren,
        hasInfo: false,
        isExpandable: false,
        isCurrency: false,
        operator: '='
      });
    }

    // Total Deemed Amount
    if (deemedRes.totalDeemedAmount !== undefined) {
      items.push({
        id: 'deemed-total-amount',
        label: 'DEEMED_RESOURCES_TOTAL_DEEMED',
        amount: deemedRes.totalDeemedAmount,
        hasInfo: false,
        isExpandable: false,
        isTotal: true,
        operator: '='
      });
    }

    return items;
  }

  /**
   * Map Deemed Income ABD sub-screen data
   */
  mapDeemedIncomeABDData(data: any): BudgetCalculationItem[] {
    const items: BudgetCalculationItem[] = [];
    
    if (!data) {
      return items;
    }
    
    // ========================================
    // SECTION 1: Income Deeming for Ineligible Spouse
    // ========================================
    if (data?.incomeDeemingForIneligSpouse) {
      const spouseData = data.incomeDeemingForIneligSpouse;
      
      // Section Header
      items.push({
        id: 'header-spouse',
        label: 'DEEMED_INCOME_SPOUSE_SECTION',
        amount: '',
        hasInfo: false,
        isHeader: true,
        isExpandable: false
      });

        items.push({
        id: 'ineligible-children',
        label: 'DEEMED_INCOME_INELIGIBLE_CHILDREN',
        amount: '',
        hasInfo: false,
        isExpandable: false,
        isSectionHeader: true,
        isCurrency: false
      });
      
      // 1.1 Ineligible Children - Loop through ALL children
      if (spouseData.ineligibleChildren && Array.isArray(spouseData.ineligibleChildren) && spouseData.ineligibleChildren.length > 0) {
        spouseData.ineligibleChildren.forEach((child: any, index: number) => {
          if (!child) return;
          
          // Build children array for this child
          const childItems: BudgetCalculationItem[] = [];
          
          // 1. Living Allowance (A)
          childItems.push({
            id: `living-allowance-children-spouse-${index}`,
            label: 'DEEMED_INCOME_LIVING_ALLOWANCE_A',
            amount: child.livingAllowance ?? 0,
            hasInfo: false,
            operator: '=',
            isExpandable: false
          });

          // 2. Self-Employment Income (B)
          if (child.selfEmploymentIncomeTotal !== undefined) {
            childItems.push({
              id: `self-employment-children-spouse-${index}`,
              label: 'DEEMED_INCOME_SELF_EMPLOYMENT_INCOME_B',
              amount: child.selfEmploymentIncomeTotal ?? 0,
              hasInfo: false,
              operator: '=',
              isExpandable: true,
              deemedIncomeTableType: 'abdDeemedIncome',
              hidePersonColumn: true,
              tableData: this.mapIncomeTableData(child?.selfEmploymentIncomes),
            });
          }
          
          // 3. Earned Income (C)
          if (child.earnedIncomeTotal !== undefined) {
            childItems.push({
              id: `earned-income-children-spouse-${index}`,
              label: 'DEEMED_INCOME_EARNED_INCOME_C',
              amount: child.earnedIncomeTotal ?? 0,
              hasInfo: false,
              operator: '=',
              isExpandable: true,
              deemedIncomeTableType: 'abdDeemedIncome',
              hidePersonColumn: true,
              tableData: this.mapIncomeTableData(child?.earnedIncomes)
            });
          }
          
          // 4. Total Earned Income (D)
          childItems.push({
            id: `total-earned-income-children-spouse-${index}`,
            label: 'DEEMED_INCOME_TOTAL_EARNED_INCOME_D',
            amount: child.totalEarnedIncome ?? 0,
            hasInfo: false,
            operator: '=',
            isExpandable: false
          });
          
          // 5. Student Earned Income Exclusion (E)
          childItems.push({
            id: `student-exclusion-children-spouse-${index}`,
            label: 'DEEMED_INCOME_STUDENT_EXCLUSION_E',
            amount: child.studentEarnedIncomeExclusion ?? 0,
            hasInfo: false,
            operator: '=',
            isExpandable: false
          });
          
          // 6. Unearned Income (F)
          if (child.totalUnearnedIncome !== undefined) {
            childItems.push({
              id: `unearned-income-children-spouse-${index}`,
              label: 'DEEMED_INCOME_UNEARNED_INCOME_F',
              amount: child.totalUnearnedIncome ?? 0,
              hasInfo: false,
              operator: '=',
              isExpandable: true,
              deemedIncomeTableType: 'abdDeemedIncome',
              hidePersonColumn: true,
              tableData: this.mapIncomeTableData(child?.unearnedIncomes)
            });
          }
          
          // 7. COLA Deduction (G)
          childItems.push({
            id: `cola-deduction-children-spouse-${index}`,
            label: 'DEEMED_INCOME_COLA_DEDUCTION_G',
            amount: child.colaDeduction ?? 0,
            hasInfo: true,
            operator: '=',
            isExpandable: false,
            tooltipText: 'COLA_DEDUCTION_TOOLTIP'
          });
          
          // 8. Ineligible Child Allocation (H)
          childItems.push({
            id: `child-allocation-spouse-${index}`,
            label: 'DEEMED_INCOME_CHILD_ALLOCATION_H',
            amount: child.ineligibleChildAllocation ?? 0,
            hasInfo: false,
            operator: '=',
            isExpandable: false
          });
          
          // Child Header (expandable with children)
          const childName = child?.childName ? ` ${child.childName}` : '';
          items.push({
            id: `child-header-spouse-${index}`,
            label: `Child ${index + 1}:${childName}`,
            amount: '',
            hasInfo: false,
            isExpandable: true,
            isCurrency: false,
            isChildHeader: true,
            children: childItems
          });
          
          // 9. Total Ineligible Child Allocation (I) - Outside the child expandable
          items.push({
            id: `total-child-allocation-spouse-${index}`,
            label: 'DEEMED_INCOME_TOTAL_CHILD_ALLOCATION_I',
            amount: child.totalIneligibleChildAllocation ?? 0,
            hasInfo: false,
            operator: '=',
            isExpandable: false
          });
        });
      } else {
        items.push({
            id: `total-child-allocation`,
            label: 'DEEMED_INCOME_TOTAL_CHILD_ALLOCATION_I',
            amount: 0,
            hasInfo: false,
            operator: '=',
            isExpandable: false
          });
      }
      
      // 1.2 Ineligible Spouse
      if (spouseData?.ineligibleSpouse) {
        const spouse = spouseData.ineligibleSpouse;
        
        items.push({
          id: 'ineligible-spouse-header',
          label: 'DEEMED_INCOME_INELIGIBLE_SPOUSE',
          amount: '',
          hasInfo: false,
          isExpandable: false,
          isSectionHeader: true,
          isCurrency: false
        });
        
        // Unearned Income
        if (spouse.unearnedIncomeTotal !== undefined) {
          items.push({
            id: 'unearned-income-spouse',
            label: 'DEEMED_INCOME_UNEARNED_INCOME_I',
            amount: spouse.unearnedIncomeTotal ?? 0,
            hasInfo: false,
            operator: '=',
            isExpandable: true,
            deemedIncomeTableType: 'abdDeemedIncome',
            tableData: this.mapIncomeTableData(spouse?.unearnedIncomes)
          });
        }
        
        items.push({
          id: 'cola-deduction-spouse',
          label: 'DEEMED_INCOME_COLA_DEDUCTION_K',
          amount: spouse.colaDeduction ?? 0,
          hasInfo: true,
          tooltipText: 'COLA_DEDUCTION_TOOLTIP',
          operator: '=',
          isExpandable: false
        });
        
        items.push({
          id: 'irregular-unearned-disregard-spouse',
          label: 'DEEMED_INCOME_IRREGULAR_UNEARNED_DISREGARD_L',
          amount: spouse.irregularUnearnedIncomeDisregard ?? 0,
          hasInfo: false,
          operator: '=',
          isExpandable: false
        });
        
        items.push({
          id: 'child-support-expense-spouse',
          label: 'DEEMED_INCOME_CHILD_SUPPORT_EXPENSE_M',
          amount: spouse.childSupportMandatoryExpense ?? 0,
          hasInfo: false,
          operator: '=',
          isExpandable: false
        });
        
        items.push({
          id: 'total-child-allocation-spouse',
          label: 'DEEMED_INCOME_TOTAL_CHILD_ALLOCATION_I',
          amount: spouse.totalIneligibleChildAllocation ?? 0,
          hasInfo: false,
          operator: '=',
          isExpandable: false
        });
        
        items.push({
          id: 'cola-disregard-spouse',
          label: 'DEEMED_INCOME_COLA_DISREGARD_N',
          amount: spouse.colaDisregard ?? 0,
          hasInfo: true,
          tooltipText: 'COLA_DEDUCTION_TOOLTIP',
          operator: '=',
          isExpandable: false
        });
        
        items.push({
          id: 'spouse-deemed-unearned-spouse',
          label: 'DEEMED_INCOME_SPOUSE_DEEMED_UNEARNED',
          amount: spouse.ineligibleSpouseDeemedUnearnedIncome ?? 0,
          hasInfo: false,
          operator: '=',
          isExpandable: false
        });
        
        // Self-Employment Income
        if (spouse.selfEmploymentIncomeTotal !== undefined) {
          items.push({
            id: 'self-employment-spouse',
            label: 'DEEMED_INCOME_SELF_EMPLOYMENT_INCOME',
            amount: spouse.selfEmploymentIncomeTotal ?? 0,
            hasInfo: false,
            operator: '=',
            isExpandable: true,
            deemedIncomeTableType: 'abdDeemedIncome',
            tableData: this.mapIncomeTableData(spouse?.selfEmploymentIncomes)
          });
        }
        
        // Earned Income
        if (spouse.earnedIncomeTotal !== undefined) {
          items.push({
            id: 'earned-income-spouse',
            label: 'DEEMED_INCOME_EARNED_INCOME_R',
            amount: spouse.earnedIncomeTotal ?? 0,
            hasInfo: false,
            operator: '=',
            isExpandable: true,
            deemedIncomeTableType: 'abdDeemedIncome',
            tableData: this.mapIncomeTableData(spouse?.earnedIncomes)
          });
        }
        
        items.push({
          id: 'total-earned-income-spouse',
          label: 'DEEMED_INCOME_TOTAL_EARNED_INCOME_S',
          amount: spouse.totalEarnedIncome ?? 0,
          hasInfo: false,
          operator: '=',
          isExpandable: false
        });
        
        items.push({
          id: 'irregular-earned-disregard-spouse',
          label: 'DEEMED_INCOME_IRREGULAR_EARNED_DISREGARD_T',
          amount: spouse.irregularEarnedIncomeDisregard ?? 0,
          hasInfo: false,
          operator: '=',
          isExpandable: false
        });

         items.push({
          id: 'student-exclusion-spouse',
          label: 'DEEMED_INCOME_STUDENT_EXCLUSION_V',
          amount: spouse.studentEarnedIncomeExclusion ?? 0,
          hasInfo: false,
          operator: '=',
          isExpandable: false
        });

        items.push({
          id: 'remaining-child-support-spouse',
          label: 'DEEMED_INCOME_REMAINING_CHILD_SUPPORT_U',
          amount: spouse.remainingChildSupportMandatoryExpense ?? 0,
          hasInfo: false,
          operator: '=',
          isExpandable: false
        });
        
        items.push({
          id: 'remaining-child-allocation-spouse',
          label: 'DEEMED_INCOME_REMAINING_CHILD_ALLOCATION_X',
          amount: spouse.remainingIneligibleChildAllocation ?? 0,
          hasInfo: false,
          operator: '=',
          isExpandable: false
        });
        
        items.push({
          id: 'spouse-deemed-earned-spouse',
          label: 'DEEMED_INCOME_SPOUSE_DEEMED_EARNED',
          amount: spouse.ineligibleSpouseDeemedEarnedIncome ?? 0,
          hasInfo: false,
          operator: '=',
          isExpandable: false
        });
      }
    }
    
    // ========================================
    // SECTION 2: Income Deeming for Ineligible Parent
    // ========================================
    if (data.incomeDeemingForIneligParent) {
      const parentData = data.incomeDeemingForIneligParent;
      
      // Section Header
      items.push({
        id: 'header-parent',
        label: 'DEEMED_INCOME_PARENT_SECTION',
        amount: '',
        hasInfo: false,
        // isSectionHeader: true,
        isHeader: true,
        isExpandable: false,
        isCurrency: false
      });

      items.push({
        id: 'ineligible-children',
        label: 'DEEMED_INCOME_INELIGIBLE_CHILDREN',
        amount: '',
        hasInfo: false,
        isExpandable: false,
        isSectionHeader: true,
        isCurrency: false
      });
      
      // 2.1 Ineligible Children
      if (parentData.ineligibleChildren && Array.isArray(parentData.ineligibleChildren) && parentData.ineligibleChildren.length > 0) {
        parentData.ineligibleChildren.forEach((child: any, index: number) => {
          if (!child) return;
          
          // Build children array for this child
          const childItems: BudgetCalculationItem[] = [];
          
          // 1. Living Allowance (A)
          childItems.push({
            id: `living-allowance-children-parent-${index}`,
            label: 'DEEMED_INCOME_LIVING_ALLOWANCE_A',
            amount: child.livingAllowance ?? 0,
            hasInfo: false,
            operator: '=',
            isExpandable: false
          });
          
          // 2. Self-Employment Income (B)
          if (child.selfEmploymentIncomeTotal !== undefined) {
            childItems.push({
              id: `self-employment-children-parent-${index}`,
              label: 'DEEMED_INCOME_SELF_EMPLOYMENT_INCOME_B',
              amount: child.selfEmploymentIncomeTotal ?? 0,
              hasInfo: false,
              operator: '=',
              isExpandable: true,
              deemedIncomeTableType: 'abdDeemedIncome',
              hidePersonColumn: true,
              tableData: this.mapIncomeTableData(child?.selfEmploymentIncomes)
            });
          }
          
          // 3. Earned Income (C)
          if (child.earnedIncomeTotal !== undefined) {
            childItems.push({
              id: `earned-income-children-parent-${index}`,
              label: 'DEEMED_INCOME_EARNED_INCOME_C',
              amount: child.earnedIncomeTotal ?? 0,
              hasInfo: false,
              operator: '=',
              isExpandable: true,
              deemedIncomeTableType: 'abdDeemedIncome',
              hidePersonColumn: true,
              tableData: this.mapIncomeTableData(child?.earnedIncomes)
            });
          }
          
          // 4. Total Earned Income (D)
          childItems.push({
            id: `total-earned-income-children-parent-${index}`,
            label: 'DEEMED_INCOME_TOTAL_EARNED_INCOME_D',
            amount: child.totalEarnedIncome ?? 0,
            hasInfo: false,
            operator: '=',
            isExpandable: false
          });
          
          // 5. Student Earned Income Exclusion (E)
          childItems.push({
            id: `student-exclusion-children-parent-${index}`,
            label: 'DEEMED_INCOME_STUDENT_EXCLUSION_E',
            amount: child.studentEarnedIncomeExclusion ?? 0,
            hasInfo: false,
            operator: '=',
            isExpandable: false
          });
          
          // 6. Unearned Income (F)
          if (child.totalUnearnedIncome !== undefined) {
            childItems.push({
              id: `unearned-income-children-parent-${index}`,
              label: 'DEEMED_INCOME_UNEARNED_INCOME_F',
              amount: child.totalUnearnedIncome ?? 0,
              hasInfo: false,
              operator: '=',
              isExpandable: true,
              deemedIncomeTableType: 'abdDeemedIncome',
              hidePersonColumn: true,
              tableData: this.mapIncomeTableData(child?.unearnedIncomes)
            });
          }
          
          // 7. COLA Deduction (G)
          childItems.push({
            id: `cola-deduction-children-parent-${index}`,
            label: 'DEEMED_INCOME_COLA_DEDUCTION_G',
            amount: child.colaDeduction ?? 0,
            hasInfo: true,
            tooltipText: 'COLA_DEDUCTION_TOOLTIP',
            operator: '=',
            isExpandable: false
          });
          
          // 8. Ineligible Child Allocation (H)
          childItems.push({
            id: `child-allocation-parent-${index}`,
            label: 'DEEMED_INCOME_CHILD_ALLOCATION_H',
            amount: child.ineligibleChildAllocation ?? 0,
            hasInfo: false,
            operator: '=',
            isExpandable: false
          });
          
          // Child Header (expandable with children)
          const childName = child.childName ? ` ${child.childName}` : '';
          items.push({
            id: `child-header-parent-${index}`,
            label: `Child ${index + 1}:${childName}`,
            amount: '',
            hasInfo: false,
            isExpandable: true,
            isCurrency: false,
            isChildHeader: true,
            children: childItems
          });
          
          // 9. Total Ineligible Child Allocation (I) - Outside the child expandable
          items.push({
            id: `total-child-allocation-parent-${index}`,
            label: 'DEEMED_INCOME_TOTAL_CHILD_ALLOCATION_I',
            amount: child.totalIneligibleChildAllocation ?? 0,
            hasInfo: false,
            operator: '=',
            isExpandable: false
          });
        });
      } else {
        items.push({
            id: `total-child-allocation`,
            label: 'DEEMED_INCOME_TOTAL_CHILD_ALLOCATION_I',
            amount: 0,
            hasInfo: false,
            operator: '=',
            isExpandable: false
          });
      }
      
      // 2.2 Ineligible Parent
      if (parentData.ineligibleParent) {
        const parent = parentData.ineligibleParent;
        
        items.push({
          id: 'ineligible-parent-header',
          label: 'DEEMED_INCOME_INELIGIBLE_PARENT',
          amount: '',
          hasInfo: false,
          isExpandable: false,
          isSectionHeader: true,
          isCurrency: false
        });
        
        // Unearned Income
        if (parent.unearnedIncomeTotal !== undefined) {
          items.push({
            id: 'unearned-income-parent',
            label: 'DEEMED_INCOME_UNEARNED_INCOME_I',
            amount: parent.unearnedIncomeTotal ?? 0,
            hasInfo: false,
            operator: '=',
            isExpandable: true,
            deemedIncomeTableType: 'abdDeemedIncome',
            tableData: this.mapIncomeTableData(parent?.unearnedIncomes)
          });
        }
        
        items.push({
          id: 'cola-deduction-parent',
          label: 'DEEMED_INCOME_COLA_DEDUCTION_K',
          amount: parent.colaDeduction ?? 0,
          hasInfo: true,
          tooltipText: 'COLA_DEDUCTION_TOOLTIP',
          operator: '=',
          isExpandable: false
        });
        
        items.push({
          id: 'irregular-unearned-disregard-parent',
          label: 'DEEMED_INCOME_IRREGULAR_UNEARNED_DISREGARD_L',
          amount: parent.irregularUnearnedIncomeDisregard ?? 0,
          hasInfo: false,
          operator: '=',
          isExpandable: false
        });
        
        items.push({
          id: 'child-support-expense-parent',
          label: 'DEEMED_INCOME_CHILD_SUPPORT_EXPENSE_M',
          amount: parent.childSupportMandatoryExpense ?? 0,
          hasInfo: false,
          operator: '=',
          isExpandable: false
        });
        
        items.push({
          id: 'total-child-allocation-parent',
          label: 'DEEMED_INCOME_TOTAL_CHILD_ALLOCATION_I',
          amount: parent.totalIneligibleChildAllocation ?? 0,
          hasInfo: false,
          operator: '=',
          isExpandable: false
        });
        
        items.push({
          id: 'general-deduction-parent',
          label: 'DEEMED_INCOME_GENERAL_DEDUCTION_N',
          amount: parent.generalDeduction ?? 0,
          hasInfo: false,
          operator: '=',
          isExpandable: false
        });
        
        items.push({
          id: 'cola-disregard-parent',
          label: 'DEEMED_INCOME_COLA_DISREGARD_V',
          amount: parent.colaDisregard ?? 0,
          hasInfo: true,
          tooltipText: 'COLA_DEDUCTION_TOOLTIP',
          operator: '=',
          isExpandable: false
        });
        
        items.push({
          id: 'net-unearned-parent',
          label: 'DEEMED_INCOME_NET_UNEARNED_P',
          amount: parent.netCountableUnearnedIncome ?? 0,
          hasInfo: false,
          operator: '=',
          isExpandable: false
        });
        
        // Self-Employment Income
        if (parent.selfEmploymentIncomeTotal !== undefined) {
          items.push({
            id: 'self-employment-parent',
            label: 'DEEMED_INCOME_SELF_EMPLOYMENT_INCOME_Q',
            amount: parent.selfEmploymentIncomeTotal ?? 0,
            hasInfo: false,
            operator: '=',
            isExpandable: true,
            deemedIncomeTableType: 'abdDeemedIncome',
            tableData: this.mapIncomeTableData(parent?.selfEmploymentIncomes)
          });
        }
        
        // Earned Income
        if (parent.earnedIncomeTotal !== undefined) {
          items.push({
            id: 'earned-income-parent',
            label: 'DEEMED_INCOME_EARNED_INCOME_I',
            amount: parent.earnedIncomeTotal ?? 0,
            hasInfo: false,
            operator: '=',
            isExpandable: true,
            deemedIncomeTableType: 'abdDeemedIncome',
            tableData: this.mapIncomeTableData(parent?.earnedIncomes)
          });
        }
        
        items.push({
          id: 'total-earned-income-parent',
          label: 'DEEMED_INCOME_TOTAL_EARNED_INCOME_T',
          amount: parent.totalEarnedIncome ?? 0,
          hasInfo: false,
          operator: '=',
          isExpandable: false
        });
        
        items.push({
          id: 'irregular-earned-disregard-parent',
          label: 'DEEMED_INCOME_IRREGULAR_EARNED_DISREGARD_U',
          amount: parent.irregularEarnedIncomeDisregard ?? 0,
          hasInfo: false,
          operator: '=',
          isExpandable: false
        });
        
        items.push({
          id: 'remaining-general-deduction-parent',
          label: 'DEEMED_INCOME_REMAINING_GENERAL_DEDUCTION_V',
          amount: parent.remainingGeneralDeduction ?? 0,
          hasInfo: false,
          operator: '=',
          isExpandable: false
        });
        
        items.push({
          id: 'student-exclusion-parent',
          label: 'DEEMED_INCOME_STUDENT_EXCLUSION_W',
          amount: parent.studentEarnedIncomeExclusion ?? 0,
          hasInfo: false,
          operator: '=',
          isExpandable: false
        });
        
        items.push({
          id: 'remaining-child-support-parent',
          label: 'DEEMED_INCOME_REMAINING_CHILD_SUPPORT_X',
          amount: parent.remainingChildSupportMandatoryExpense ?? 0,
          hasInfo: false,
          operator: '=',
          isExpandable: false
        });
        
        items.push({
          id: 'remaining-child-allocation-parent',
          label: 'DEEMED_INCOME_REMAINING_CHILD_ALLOCATION_Y',
          amount: parent.remainingIneligibleChildAllocation ?? 0,
          hasInfo: false,
          operator: '=',
          isExpandable: false
        });
        
        items.push({
          id: 'earned-income-deduction-parent',
          label: 'DEEMED_INCOME_EARNED_DEDUCTION_Z',
          amount: parent.earnedIncomeDeduction ?? 0,
          hasInfo: false,
          operator: '=',
          isExpandable: false
        });
        
        items.push({
          id: 'half-deduction-parent',
          label: 'DEEMED_INCOME_HALF_DEDUCTION_Z',
          amount: parent.oneHalfDeduction ?? 0,
          hasInfo: false,
          operator: '=',
          isExpandable: false
        });
        
        items.push({
          id: 'net-earned-parent',
          label: 'DEEMED_INCOME_NET_EARNED_AA',
          amount: parent.netCountableEarnedIncome ?? 0,
          hasInfo: false,
          operator: '=',
          isExpandable: false
        });
        
        items.push({
          id: 'total-net-parent',
          label: 'DEEMED_INCOME_TOTAL_NET_PARENT_AB',
          amount: parent.totalNetIncomeOfIneligibleParent ?? 0,
          hasInfo: false,
          operator: '=',
          isExpandable: false
        });
        
        items.push({
          id: 'federal-benefit-rate-parent',
          label: 'DEEMED_INCOME_FEDERAL_BENEFIT_RATE_AC',
          amount: parent.federalBenefitRate ?? 0,
          hasInfo: false,
          operator: '=',
          isExpandable: false
        });
        
        // Number of eligible children - NO CURRENCY
        items.push({
          id: 'num-eligible-children-parent',
          label: 'DEEMED_INCOME_NUM_ELIGIBLE_CHILDREN_AD',
          amount: parent.numberOfEligibleChildren ?? 0,
          hasInfo: false,
          operator: '=',
          isExpandable: false,
          isCurrency: false
        });
        
        items.push({
          id: 'parent-deemed-income',
          label: 'DEEMED_INCOME_PARENT_DEEMED_INCOME_AE',
          amount: parent.ineligibleParentsDeemedIncome ?? 0,
          hasInfo: false,
          operator: '=',
          isExpandable: false
        });
      }
    }
    
    return items;
  }

  /**
   * Map budget data to resource items for UI display
   */
  mapResourceItems(budget: Budget): BudgetCalculationItem[] {
    const items: BudgetCalculationItem[] = [];

    if (!budget.resources) {
      return items;
    }

    const resources = budget?.resources;

    // Liquid Resources (expandable, shows + for expand/collapse)
    if (resources.liquidResourcesTotal !== undefined) {
      items.push({
        id: 'liquid-resources',
        label: 'RESOURCES_LIQUID',
        amount: resources.liquidResourcesTotal,
        hasInfo: false,
        isExpandable: true,
        isCurrency: true,
        tableData: this.mapResourceTableData(resources.liquidResources),
        resourceType: 'liquid',
        operator: '+'
      });
    }

    // Non-Liquid Resources (expandable, shows + for expand/collapse)
    if (resources.nonLiquidResourcesTotal !== undefined) {
      items.push({
        id: 'non-liquid',
        label: 'RESOURCES_NON_LIQUID',
        amount: resources.nonLiquidResourcesTotal,
        hasInfo: false,
        isExpandable: true,
        isCurrency: true,
        tableData: this.mapResourceTableData(resources.nonLiquidResources),
        resourceType: 'nonLiquid',
        operator: '+'
      });
    }

    // Burial Reserve Exclusion (shows - operator)
    if (resources.burialReserveExclusion !== undefined) {
      items.push({
        id: 'burial-reserve',
        label: 'RESOURCES_BURIAL_RESERVE',
        amount: resources.burialReserveExclusion,
        hasInfo: false,
        isExpandable: false,
        isCurrency: true,
        operator: '-'
      });
    }

    // Vehicle Resources (expandable, shows + for expand/collapse)
    if (resources.vehicleResourceTotal !== undefined) {
      items.push({
        id: 'vehicle-resources',
        label: 'RESOURCES_VEHICLE',
        amount: resources.vehicleResourceTotal,
        hasInfo: false,
        isExpandable: true,
        isCurrency: true,
        operator: '+',
        tableData: this.mapResourceTableData(resources.vehicleResources),
        resourceType: 'vehicle'
      });
    }

    // Vehicle Resource Exclusion (shows - operator)
    if (resources.vehicleResourceExclusion !== undefined && budget?.type === 'FM') {
      items.push({
        id: 'vehicle-exclusion',
        label: 'RESOURCES_VEHICLE_EXCLUSION',
        amount: resources.vehicleResourceExclusion,
        hasInfo: false,
        isExpandable: false,
        isCurrency: true,
        operator: '-'
      });
    }

    // Countable Resources Amount (shows = operator)
    if (resources.countableResourceAmount !== undefined) {
      items.push({
        id: 'countable-amount',
        label: 'RESOURCES_COUNTABLE_AMOUNT',
        amount: resources.countableResourceAmount,
        hasInfo: false,
        isExpandable: false,
        isCurrency: true,
        operator: '='
      });
    }

    // Deemed Resources (shows + operator, link to modal)
    if (resources.deemedResources !== undefined) {
      items.push({
        id: 'deemed-resources',
        label: 'RESOURCES_DEEMED',
        amount: resources.deemedResources,
        hasInfo: false,
        isExpandable: false,
        isLink: budget?.deemedResources !== null,
        isCurrency: true,
        operator: '+',
        subScreenType: 'deemedResources',
        coe: budget?.coe,
        indivId: (budget as any)?.indvId
      });
    }

    // LTCP Deduction (shows - operator)
    if (resources.ltcpDeduction !== undefined) {
      items.push({
        id: 'ltcp-deduction',
        label: 'RESOURCES_LTCP_DEDUCTION',
        amount: resources.ltcpDeduction,
        hasInfo: true,
        isExpandable: false,
        isCurrency: true,
        operator: '=',
        tooltipText: 'RESOURCES_LTCP_DEDUCTION_TOOLTIP'
      });
    }

    // CSRMA (CSHCN) (shows = operator)
    if (resources.csrma !== undefined) {
      items.push({
        id: 'cshcn',
        label: 'RESOURCES_CSRMA',
        amount: resources.csrma,
        hasInfo: true,
        isExpandable: false,
        isCurrency: true,
        operator: '=',
        tooltipText: 'RESOURCES_CSRMA_DEDUCTION_TOOLTIP'
      });
    }

    // Total Countable Resource Amount (shows = operator)
    if (resources.totalCountableResourceAmount !== undefined) {
      items.push({
        id: 'total-countable',
        label: 'RESOURCES_TOTAL_COUNTABLE',
        amount: resources.totalCountableResourceAmount,
        hasInfo: false,
        isExpandable: false,
        isTotal: true,
        isCurrency: true,
        operator: '='
      });
    }

    // Resource Limit (shows = operator)
    if (resources.resourceLimit !== undefined) {
      items.push({
        id: 'resource-limit',
        label: 'RESOURCES_LIMIT',
        amount: resources.resourceLimit,
        hasInfo: false,
        isExpandable: false,
        isCurrency: true,
        operator: '='
      });
    }

    // Resource Eligibility Result (text only, no $ or operator)
    if (resources.resourceEligibilityResult !== undefined) {
      items.push({
        id: 'resource-eligibility',
        label: 'RESOURCES_ELIGIBILITY_RESULT',
        amount: resources.resourceEligibilityResult,
        hasInfo: false,
        isExpandable: false,
        isResult: true,
        isCurrency: false // This is text (Pass/Fail), not currency
      });
    }

    return items;
  }

  /**
   * Map income details to table format
   */
  private mapIncomeTableData(incomeDetails: IncomeDetail[] | undefined): any[] {
    if (!incomeDetails || incomeDetails.length === 0) {
      return [{
        person: 'N/A',
        type: 'N/A',
        adjustedSpouseAmount: 0,
        amount: 0,
        excludedAmount: 0,
        countableAmount: 0
      }];
    }

    return incomeDetails.map(detail => ({
      person: detail?.person ? detail.person : 'N/A',
      type: detail?.type ? detail.type : 'N/A',
      adjustedSpouseAmount: detail?.adjustedSpouseAmount ? detail.adjustedSpouseAmount : 0,
      amount: detail?.amount ? detail.amount : 0,
      excludedAmount: detail?.excludedAmount ? detail.excludedAmount : 0,
      countableAmount: detail?.countableAmount ? detail.countableAmount : 0
    }));
  }

  /**
   * Map resource details to table format with seqNum grouping for joint ownership
   * Resources with the same seqNum are grouped together, with resourceType shown only on the first row
   */
  private mapResourceTableData(resourceDetails: ResourceDetail[] | undefined): any[] {
    if (!resourceDetails || resourceDetails.length === 0) {
      return [{
        resourceType: 'N/A',
        description: 'N/A',
        specialConsiderations: 'N/A',
        value: 0,
        owner: 'N/A',
        percentOwnership: 'N/A',
        excludedAmount: 0,
        countableAmount: 0
      }];
    }

    // Sort by seqNum to ensure grouped resources (joint ownership) are consecutive
    // Resources without seqNum are placed at the end
    const sortedDetails = [...resourceDetails]?.sort((a, b) => {
      const seqA = a?.seqNum ?? Number.MAX_SAFE_INTEGER; // Put nulls at end
      const seqB = b?.seqNum ?? Number.MAX_SAFE_INTEGER;
      return seqA - seqB;
    });
    
    // Track which seqNum values we've seen to identify parent vs child rows
    const seenSeqNums = new Set<number>();
    return sortedDetails.map(detail => {
      const seqNum = detail?.seqNum;
      let resourceType;
      if (seqNum !== null && seqNum !== undefined) {
        resourceType = seenSeqNums.has(seqNum) ? null : (detail?.resourceType || 'N/A');
        seenSeqNums.add(seqNum);
      } else {
        resourceType = detail?.resourceType || 'N/A';
      }
      
      return {
        resourceType: resourceType,
        description: detail?.description ? detail?.description : 'N/A',
        specialConsiderations: detail?.specialConsiderations ? detail?.specialConsiderations : 'N/A',
        value: (detail?.value !== null && detail?.value !== undefined) ? detail?.value : 0,
        owner: detail?.owner ? detail?.owner : 'N/A',
        percentOwnership: (detail?.percentOwnership !== null && detail?.percentOwnership !== undefined) ? detail?.percentOwnership : 'N/A',
        excludedAmount: (detail?.excludedInaccessibleAmount !== null && detail?.excludedInaccessibleAmount !== undefined) ? detail?.excludedInaccessibleAmount : 0,
        countableAmount: (detail?.countableAmount !== null && detail?.countableAmount !== undefined) ? detail?.countableAmount : 0
      };
    });
  }

  /**
   * ============================================
   * SUB-SCREEN MAPPERS
   * ============================================
   */

  /**
   * Map In-Kind Support and Maintenance sub-screen data
   */
  mapInKindSupportData(data: InKindSupportMaintenance): BudgetCalculationItem[] {
    const items: BudgetCalculationItem[] = [];

    if (data.applicantLivingWithNonSpouseAdult !== undefined) {
      items.push({
        id: 'living-with-adult',
        label: 'ISM_LIVING_WITH_ADULT',
        amount: data.applicantLivingWithNonSpouseAdult,
        hasInfo: false,
        isExpandable: false,
        isCurrency: false,
        isResult: true
      });
    }

    if (data.totalNumberOfPeopleInHousehold !== undefined) {
      items.push({
        id: 'household-count',
        label: 'ISM_HOUSEHOLD_COUNT',
        amount: data.totalNumberOfPeopleInHousehold,
        hasInfo: false,
        isExpandable: false,
        isCurrency: false,
        isResult: true
      });
    }

    if (data.pmvOrVtrValue !== undefined) {
      items.push({
        id: 'pmv-vtr',
        label: 'ISM_PMV_VTR_VALUE',
        amount: data.pmvOrVtrValue,
        hasInfo: true,
        isExpandable: false,
        isCurrency: false,
        tooltipText: 'ISM_PMV_VTR_TOOLTIP',
        isResult: true
      });
    }

    // Inside ISM Calculation Section
    items.push({
      id: 'inside-ism-header',
      label: 'INSIDE_ISM_CALCULATION',
      amount: '',
      hasInfo: false,
      isExpandable: false,
      isHeader: true,
      isCurrency: false
    });

    if (data.totalExpensesPaidByHousehold !== undefined) {
      items.push({
        id: 'household-expenses',
        label: 'ISM_HOUSEHOLD_EXPENSES',
        amount: data.totalExpensesPaidByHousehold,
        hasInfo: false,
        isExpandable: false,
        operator: '='
      });
    }

    if (data.applicantFairShareOfHouseholdExpenses !== undefined) {
      items.push({
        id: 'fair-share',
        label: 'ISM_FAIR_SHARE',
        amount: data.applicantFairShareOfHouseholdExpenses,
        hasInfo: false,
        isExpandable: false,
        operator: '='

      });
    }

    if (data.amountPaidByApplicantTowardsHouseholdExpenses !== undefined) {
      items.push({
        id: 'amount-paid',
        label: 'ISM_AMOUNT_PAID',
        amount: data.amountPaidByApplicantTowardsHouseholdExpenses,
        hasInfo: false,
        isExpandable: false,
        operator: '='
      });
    }

    if (data.insideGrossIsmAmount !== undefined) {
      items.push({
        id: 'inside-gross',
        label: 'ISM_INSIDE_GROSS',
        amount: data.insideGrossIsmAmount,
        hasInfo: false,
        isExpandable: false,
        operator: '='
      });
    }

    // Outside ISM Calculation Section
    items.push({
      id: 'outside-ism-header',
      label: 'OUTSIDE_ISM_CALCULATION',
      amount: '',
      hasInfo: false,
      isExpandable: false,
      isHeader: true,
      isCurrency: false
    });

    if (data.totalExpensesPaidByOutsideHousehold !== undefined) {
      items.push({
        id: 'outside-expenses',
        label: 'ISM_OUTSIDE_EXPENSES',
        amount: data.totalExpensesPaidByOutsideHousehold,
        hasInfo: false,
        isExpandable: false,
        operator: '='
      });
    }

    if (data.outsideGrossIsmAmount !== undefined) {
      items.push({
        id: 'outside-gross',
        label: 'ISM_OUTSIDE_GROSS',
        amount: data.outsideGrossIsmAmount,
        hasInfo: false,
        isExpandable: false,
        operator: '='
      });
    }


    // Net ISM Amount Calculation Section
    items.push({
      id: 'net-ism-header',
      label: 'NET_ISM_AMOUNT_CALCULATION',
      amount: '',
      hasInfo: false,
      isExpandable: false,
      isHeader: true,
      isCurrency: false
    });

     if (data.totalGrossIsmAmount !== undefined) {
      items.push({
        id: 'total-gross',
        label: 'ISM_TOTAL_GROSS',
        amount: data.totalGrossIsmAmount,
        hasInfo: false,
        isExpandable: false,
        isTotal: true,
        operator: '='
      });
    }

    if (data.presumedMaximumValue !== undefined) {
      items.push({
        id: 'presumed-max',
        label: 'ISM_PRESUMED_MAX',
        amount: data.presumedMaximumValue,
        hasInfo: false,
        isExpandable: false,
        operator: '='
      });
    }

    if (data.valueOfOneThirdReduction !== undefined) {
      items.push({
        id: 'vtr',
        label: 'ISM_VTR',
        amount: data.valueOfOneThirdReduction,
        hasInfo: false,
        isExpandable: false,
        operator: '='
      });
    }

    if (data.netIsmAmount !== undefined) {
      items.push({
        id: 'net-ism',
        label: 'ISM_NET_AMOUNT',
        amount: data.netIsmAmount,
        hasInfo: false,
        isExpandable: false,
        isTotal: true,
        operator: '='
      });
    }

    return items;
  }

  /**
   * Map Qualified Income Trust sub-screen data
   */
  mapQualifiedIncomeTrustData(data: QualifiedIncomeTrust): BudgetCalculationItem[] {
    const items: BudgetCalculationItem[] = [];

    if (data.totalCountableIncomeBeforeQit !== undefined) {
      items.push({
        id: 'qit-before',
        label: 'QIT_INCOME_BEFORE',
        amount: data.totalCountableIncomeBeforeQit,
        hasInfo: false,
        isExpandable: false,
        operator: '=',
      });
    }

    if (data.amountFundingQit !== undefined) {
      items.push({
        id: 'qit-funding',
        label: 'QIT_FUNDING_AMOUNT',
        amount: data.amountFundingQit,
        hasInfo: false,
        isExpandable: false,
        operator: '=',
      });
    }

    if (data.totalCountableIncome !== undefined) {
      items.push({
        id: 'qit-countable',
        label: 'QIT_TOTAL_COUNTABLE',
        amount: data.totalCountableIncome,
        hasInfo: false,
        isExpandable: false,
        isTotal: true,
        operator: '='
      });
    }

    if (data.medicaidIncomeCap !== undefined) {
      items.push({
        id: 'qit-cap',
        label: 'QIT_MEDICAID_CAP',
        amount: data.medicaidIncomeCap,
        hasInfo: false,
        isExpandable: false,
        operator: '='
      });
    }

    if (data.incomeEligibilityTestResult !== undefined) {
      items.push({
        id: 'qit-result',
        label: 'QIT_ELIGIBILITY_RESULT',
        amount: data.incomeEligibilityTestResult,
        hasInfo: false,
        isExpandable: false,
        isResult: true,
        isCurrency: false
      });
    }

    return items;
  }

  /**
   * Map Deemed Income FM sub-screen data
   */
  mapDeemedIncomeFMData(data: DeemedIncomeFM): BudgetCalculationItem[] {
    const items: BudgetCalculationItem[] = [];

    items.push({
      id: 'deemedIncomeFm-header',
      label: 'DEEMED_INCOME_FM_HEADER',
      amount: '',
      hasInfo: false,
      isExpandable: false,
      isHeader: true,
      isCurrency: false
    });

    // Self Employment Income
    if (data.netCountableSelfEmploymentIncomeTotal !== undefined) {
      items.push({
        id: 'deemed-fm-self-employment',
        label: 'DEEMED_FM_SELF_EMPLOYMENT',
        amount: data.netCountableSelfEmploymentIncomeTotal,
        hasInfo: false,
        isExpandable: true,
        operator: '=',
        tableData: this.mapIncomeTableData(data.netCountableSelfEmploymentIncomes)
      });
    }

    // Earned Income
    if (data.netCountableEarnedIncomeTotal !== undefined) {
      items.push({
        id: 'deemed-fm-earned',
        label: 'DEEMED_FM_EARNED_INCOME',
        amount: data.netCountableEarnedIncomeTotal,
        hasInfo: false,
        isExpandable: true,
        operator: '=',
        tableData: this.mapIncomeTableData(data.netCountableEarnedIncomes)
      });
    }

    if (data.irregularEarnedIncomeDisregard !== undefined) {
      items.push({
        id: 'deemed-fm-irregular-earned',
        label: 'DEEMED_FM_IRREGULAR_EARNED',
        amount: data.irregularEarnedIncomeDisregard,
        hasInfo: false,
        isExpandable: false,
        operator: '='
      });
    }

    if (data.standardWorkExpenseDeduction !== undefined) {
      items.push({
        id: 'deemed-fm-work-expense',
        label: 'DEEMED_FM_WORK_EXPENSE',
        amount: data.standardWorkExpenseDeduction,
        hasInfo: true,
        isExpandable: false,
        operator: '-'
      });
    }

    if (data.dependentCareDeduction !== undefined) {
      items.push({
        id: 'deemed-fm-dependent-care',
        label: 'DEEMED_FM_DEPENDENT_CARE',
        amount: data.dependentCareDeduction,
        hasInfo: true,
        isExpandable: false,
        operator: '-'
      });
    }

    if (data.totalEarnedIncome !== undefined) {
      items.push({
        id: 'deemed-fm-total-earned',
        label: 'DEEMED_FM_TOTAL_EARNED',
        amount: data.totalEarnedIncome,
        hasInfo: false,
        isExpandable: false,
        isTotal: true,
        operator: '='
      });
    }

    // Unearned Income
    if (data.netCountableUnearnedIncomeTotal !== undefined) {
      items.push({
        id: 'deemed-fm-unearned',
        label: 'DEEMED_FM_UNEARNED_INCOME',
        amount: data.netCountableUnearnedIncomeTotal,
        hasInfo: false,
        isExpandable: true,
        operator: '=',
        tableData: this.mapIncomeTableData(data.netCountableUnearnedIncomes)
      });
    }

    if (data.irregularUnearnedIncomeDisregard !== undefined) {
      items.push({
        id: 'deemed-fm-irregular-unearned',
        label: 'DEEMED_FM_IRREGULAR_UNEARNED',
        amount: data.irregularUnearnedIncomeDisregard,
        hasInfo: false,
        isExpandable: false,
        operator: '='
      });
    }

    if (data.childSupportDisregard !== undefined) {
      items.push({
        id: 'deemed-fm-child-support',
        label: 'DEEMED_FM_CHILD_SUPPORT',
        amount: data.childSupportDisregard,
        hasInfo: false,
        isExpandable: false,
        operator: '-'
      });
    }

    if (data.totalUnearnedIncome !== undefined) {
      items.push({
        id: 'deemed-fm-total-unearned',
        label: 'DEEMED_FM_TOTAL_UNEARNED',
        amount: data.totalUnearnedIncome,
        hasInfo: false,
        isExpandable: false,
        isTotal: true,
        operator: '='
      });
    }

    if (data.totalCountableIncome !== undefined) {
      items.push({
        id: 'deemed-fm-total-countable',
        label: 'DEEMED_FM_TOTAL_COUNTABLE',
        amount: data.totalCountableIncome,
        hasInfo: false,
        isExpandable: false,
        isTotal: true,
        operator: '='
      });
    }

    // Child Support Expenses
    if (data.childSupportMandatoryExpensesTotal !== undefined) {
      items.push({
        id: 'deemed-fm-child-support-expense',
        label: 'DEEMED_FM_CHILD_SUPPORT_EXPENSES',
        amount: data.childSupportMandatoryExpensesTotal,
        hasInfo: true,
        isExpandable: false,
        operator: '=',
        // tableData: this.mapChildSupportTableData(data.childSupportMandatoryExpenses)
      });
    }

    if (data.totalDeemedIncome !== undefined) {
      items.push({
        id: 'deemed-fm-total-deemed',
        label: 'DEEMED_FM_TOTAL_DEEMED',
        amount: data.totalDeemedIncome,
        hasInfo: false,
        isExpandable: false,
        isTotal: true,
        operator: '='
      });
    }

    return items;
  }

  /**
   * Map Medical Spenddown sub-screen data
   */
  mapMedicalSpenddownData(data: MedicalSpenddown): BudgetCalculationItem[] {
    const items: BudgetCalculationItem[] = [];

     // Spenddown Calculation Section Header
      items.push({
        id: 'spenddown-header',
        label: 'SPENDDOWN_CALCULATION',
        amount: '',
        hasInfo: false,
        isExpandable: false,
        isHeader: true,
        isCurrency: false
      });

    if (data?.totalNetCountableIncome !== undefined) {
      items.push({
        id: 'spenddown-net-income',
        label: 'SPENDDOWN_NET_INCOME',
        amount: data.totalNetCountableIncome,
        hasInfo: false,
        isExpandable: false,
        operator: '='
      });
    }

    if (data?.incomeTestLimit !== undefined) {
      items.push({
        id: 'spenddown-limit',
        label: 'SPENDDOWN_INCOME_LIMIT',
        amount: data.incomeTestLimit,
        hasInfo: false,
        isExpandable: false,
        operator: '='
      });
    }

    if (data?.spenddown !== undefined) {
      items.push({
        id: 'spenddown-amount',
        label: 'SPENDDOWN_AMOUNT',
        amount: data.spenddown,
        hasInfo: false,
        isExpandable: false,
        isTotal: false,
        operator: '=',
      });
    }

    if (data?.currentYearsMedicalExpenses !== undefined) {
      items.push({
        id: 'spenddown-current-expenses',
        label: 'SPENDDOWN_CURRENT_EXPENSES',
        amount: data.currentYearsMedicalExpenses,
        hasInfo: false,
        isExpandable: false,
        operator: '='
      });
    }

    if (data?.previousYearsCarryForwardAmount !== undefined) {
      items.push({
        id: 'spenddown-carry-forward',
        label: 'SPENDDOWN_CARRY_FORWARD',
        amount: data.previousYearsCarryForwardAmount,
        hasInfo: false,
        isExpandable: false,
        operator: '=',
      });
    }

    if (data?.totalMedicalExpenses !== undefined) {
      items.push({
        id: 'spenddown-total-expenses',
        label: 'SPENDDOWN_TOTAL_EXPENSES',
        amount: data.totalMedicalExpenses,
        hasInfo: false,
        isExpandable: false,
        isTotal: false,
        operator: '='
      });
    }

     if (data?.unpaidPreviousCarryFwdAmt !== undefined) {
      items.push({
        id: 'spenddown-unpaid-previous-carry-forward',
        label: 'SPENDDOWN_UNPAID_PREVIOUS_CARRY_FORWARD',
        amount: data.unpaidPreviousCarryFwdAmt,
        hasInfo: false,
        isExpandable: false,
        isTotal: false,
        operator: '='
      });
    }

    if (data?.remainingSpenddownNeeded !== undefined) {
      items.push({
        id: 'spenddown-remaining',
        label: 'SPENDDOWN_REMAINING',
        amount: data.remainingSpenddownNeeded,
        hasInfo: false,
        isExpandable: false,
        operator: '='
      });
    }

    if (data?.spenddownMet !== undefined) {
      items.push({
        id: 'spenddown-met',
        label: 'SPENDDOWN_MET',
        amount: data.spenddownMet,
        hasInfo: false,
        isExpandable: false,
        isResult: true,
        isCurrency: false
      });
    }

    if (data?.dateSpenddownMet !== undefined) {
      items.push({
        id: 'spenddown-date-met',
        label: 'SPENDDOWN_DATE_MET',
        amount: this.formatDate(data.dateSpenddownMet),
        hasInfo: false,
        isExpandable: false,
        isResult: true,
        isCurrency: false
      });
    }

    if (data?.futureCarryForwardAmount !== undefined) {
      items.push({
        id: 'spenddown-future-carry',
        label: 'SPENDDOWN_FUTURE_CARRYFORWARD',
        amount: data.futureCarryForwardAmount,
        hasInfo: false,
        isExpandable: false,
        operator: '='
      });
    }

    return items;
  }

  /**
   * Map child support expense details to table format
   */
  // private mapChildSupportTableData(expenses: any[] | undefined): any[] {
  //   if (!expenses || expenses.length === 0) {
  //     return [{
  //       expensesPerson: 'N/A',
  //       expensesForPerson: 'N/A',
  //       expensesAmount: 0,
  //       expensesCountableAmount: 0
  //     }];
  //   }

  //   return expenses.map(expense => ({
  //     expensesPerson: expense?.person ? expense.person : 'N/A',
  //     expensesForPerson: expense?.forPerson ? expense.forPerson : 'N/A',
  //     expensesAmount: expense?.amount ? expense.amount : 0,
  //     expensesCountableAmount: expense?.countableAmount ? expense.countableAmount : 0
  //   }));
  // }

  /**
   * Format date from YYYY-MM-DD to MM/DD/YYYY
   */
  private formatDate(dateString: string): string {
    if (!dateString) return dateString;
    
    // Check if it's in YYYY-MM-DD format
    const datePattern = /^\d{4}-\d{2}-\d{2}$/;
    if (datePattern.test(dateString)) {
      const [year, month, day] = dateString.split('-');
      return `${month}/${day}/${year}`;
    }
    
    // Return as-is if not in expected format
    return dateString;
  }
}

